import React from 'react';
import { Nav } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { LayoutDashboard, LineChart, MessageSquare, Settings, User2 } from 'lucide-react';

import LogoutIcon from '../../assets/svgComponents/LogoutIcon';
import PaymentsTabIcon from '../../assets/svgComponents/PaymentsTabIcon';
import { signOut } from '../../redux/actions/authActions';
import adminAppUrl from '../../routing/adminApp';
import urls from '../../routing/urls';
import { selectCurrentUserOrganizations } from '../../selectors/authSelectors';
import { selectCurrentOrganization } from '../../selectors/organizationSelectors';

import DropDown from './DropDown/DropDown';
import SideMenuBtn from './SideMenuBtn/SideMenuBtn';
import SideMenuLink from './SideMenuLink/SideMenuLink';

import styles from './SideMenu.module.scss';

const DEFAULT_PAGE = 1;

const SideMenu = () => {
  const { t } = useTranslation('common');
  const currentOrg = useSelector(selectCurrentOrganization);
  const currentUserOrganizations = useSelector(selectCurrentUserOrganizations);
  const dispatch = useDispatch();
  const location = useLocation();

  const tabsData = [
    { title: t('navigation.dashboard'), icon: LayoutDashboard, path: urls.dashboard },
    { title: t('navigation.browse'), icon: MessageSquare, path: `${urls.browse}?page=${DEFAULT_PAGE}` },
    { title: t('navigation.tracker'), icon: LineChart, path: urls.tracker },
    { title: t('navigation.account'), icon: User2, path: urls.profile },
    { title: t('navigation.payments'), icon: PaymentsTabIcon, path: urls.paymentRecords },
  ];

  const renderTabs = (tabs) =>
    tabs.map((data) => (
      <Nav.Item key={`${data.path}`} className="mb-1">
        <SideMenuLink title={data.title} icon={data.icon} path={data.path} />
      </Nav.Item>
    ));

  const logoutUser = () => {
    localStorage.removeItem('selectedOrganization');
    localStorage.removeItem('prevOrganization');
    dispatch(signOut());
  };

  const onLaunchBoonBtnClick = (appUrl) => {
    if (!currentOrg?.uuid) return;

    const url = `${appUrl}/dashboard?orgUuid=${currentOrg.uuid}`;
    localStorage.removeItem('activeTab');

    window.open(url, '_self');
  };

  const setDefaultActiveKey = () => {
    const storedTab = localStorage.getItem('activeTab');
    if (storedTab) {
      return storedTab;
    }

    const path = location.pathname;
    if (path.includes('browse') || path.includes('jobs')) return `${urls.browse}?page=${DEFAULT_PAGE}`;
    if (path.includes('tracker')) return '/tracker';
    if (path.includes('account')) return '/account/profile';
    if (path.includes('dashboard')) return '/dashboard';
  };

  const handleTabSelect = (key) => {
    localStorage.setItem('activeTab', key);
  };

  const checkIfAdmin = (orgUuid) => {
    const userOrg = currentUserOrganizations?.find((org) => org.organization.uuid === orgUuid);
    return userOrg && userOrg.role_name !== 'member';
  };

  return (
    <div className={`${styles.sideMenu} d-flex flex-column`}>
      <div className={`container  ${styles.containerDiv}`}>
        <DropDown />
      </div>

      <div className="container flex-grow-1">
        <Nav variant="pills" activeKey={setDefaultActiveKey()} className="flex-column pt-3" onSelect={handleTabSelect}>
          {renderTabs(tabsData)}
          {currentOrg?.uuid && checkIfAdmin(currentOrg.uuid) && (
            <Nav.Item>
              <Nav.Link onClick={() => onLaunchBoonBtnClick(adminAppUrl)}>
                <span className="pe-3">
                  <Settings />
                </span>
                <span>{t('navigation.adminApp')}</span>
              </Nav.Link>
            </Nav.Item>
          )}
        </Nav>
      </div>
      <div className={`${styles.logoutButton} container d-flex flex-column`}>
        <SideMenuBtn data-testid="logout-button" title={t('logout')} icon={LogoutIcon} handleClick={logoutUser} />
      </div>
    </div>
  );
};

export default SideMenu;
